import { Badge } from "@comps/ui/badge";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@comps/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import Field, { FieldChoice, fieldChoiceByNames, FieldType, TYPE_ITEMS_CHOICE_OPTIONS } from "@dto/fields";
import { TW_ChoiceBadge, TW_ChoiceBadgeDisabled, TW_ChoiceCell, TW_ChoiceEditionPopoverContent, TW_ChoiceItem, TW_ChoiceItemSelected, TW_ChoiceItemText, TW_ChoiceScrollArea, TW_CommonCommandInput } from "@utils/twClasses";
import { containsPosition, isArray, nextPosition, posToString, prevPosition, samePosition } from "@utils/types";
import { memo, useEffect, useRef, useState } from "react";
import { useIsMobile } from "@comps/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { useDataStore, useModelStore, useUserStore } from "src/store";
import { CellProps } from "./GridCell";
import { EditRightAccess, hasGlobalRightAccess, ObjectRightType, ViewRightType } from "@dto/Users";


export function ChoiceCell({ field, item, position, updateValue, isEditing }: CellProps) {

    const colNumber = useDataStore(state => state.colNumber);
    const edit = useDataStore(state => state.edit);
    const focus = useDataStore(state => state.focus);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const isMobile = useIsMobile();
    const [value, setValue] = useState<string[] | undefined>(item?.value ? item.value as string[] : undefined);
    const [searchValue, setSearchValue] = useState("");
    const userRole = useModelStore(state => state.userRole);
    const user = useUserStore(state => state.user);
    const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
    const selectedView = useModelStore(state => state.selectedView);
    const hasEditAccessOnObject = hasGlobalRightAccess(user, userRole, ObjectRightType, EditRightAccess, selectedObjectInfos?.object?._key);
    const hasEditAccessOnView = hasGlobalRightAccess(user, userRole, ViewRightType, EditRightAccess, selectedView?._key);
    const hasEditAccess = hasEditAccessOnObject && hasEditAccessOnView;
    const readOnly = item.readOnly || !hasEditAccess ;

    useEffect(() => {
        if (item?.value !== value) {
            setValue(item?.value ? item.value as string[] : undefined);
        }
    }, [item]);

    useEffect(() => {
        if (isEditing) {
            // Seed the search with the character typed to start editing (stored
            // by the grid in editInitialKey) so the first letter isn't lost; the
            // controlled cmdk input syncs it into its filter on mount.
            const initialKey = useDataStore.getState().editInitialKey;
            if (initialKey && initialKey.length === 1) {
                setSearchValue(initialKey);
            }
        }
    }, [isEditing]);

    function validate(fieldChoices: string[]) {

        if (readOnly || !isEditing) {
            return;
        }

        // if the value is the same as the item value, do nothing
        if (fieldChoices === value) {
            edit(position);
            return;
        }

        if (field.type !== FieldType.MultiChoice) {
            edit(position);
        }

        let newValue: string[] | null = fieldChoices;
        if (!fieldChoices || fieldChoices.length === 0) {
            newValue = null;
        }
        const newItem = { ...item, value: newValue };
        setValue(newValue ?? undefined);
        updateValue(newItem, newValue);
        setSearchValue("");
        if (field.type === FieldType.Choice) {
            setIsDialogOpen(false);
        }
    }


    const options = field.typeItems?.get(TYPE_ITEMS_CHOICE_OPTIONS);
    const choiceByNames = fieldChoiceByNames(options);

    function cancel() {
        setSearchValue("");
        focus(position);
    }

    function onEnterKeyPress(event: React.KeyboardEvent) {
        if (readOnly) {
            return;
        }
        switch (event.key) {
            case "Backspace":
            case "Delete":
                if (!isEditing) {
                    validate([]);
                }
                break;
            case "Enter":
                event.preventDefault();
                if (!isEditing) {
                    edit(position);
                }
                break;
            case "Tab":
                let next = null;
                if (event.shiftKey) {
                    next = prevPosition(position, colNumber);
                } else {
                    next = nextPosition(position, colNumber);
                }
                focus(next);
                break;
            case "Escape":
                event.preventDefault();
                cancel();
                break;
            default:
                if (event.ctrlKey) {
                    // Copy/paste (Ctrl+C / Ctrl+V) bubble up to GridCell, which
                    // handles them for the whole selection.
                    return;
                }
                if (event.key === "Alt") {
                    return;
                }
                // Fallback when the trigger itself is focused (the grid normally
                // routes the first keystroke through GridCell, which seeds via
                // editInitialKey). The input is controlled, so seed the state —
                // assigning .value directly was the original lost-first-letter bug.
                if (!isEditing && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
                    setSearchValue(event.key);
                    edit(position);
                }
        }
    }

    function onTriggerClick(event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) {
        if (readOnly) {
            return;
        }

        const currentFocusPos = useDataStore.getState().focusPos;
        const isSelected = currentFocusPos && currentFocusPos.length === 1 && samePosition(currentFocusPos[0], position);
        if (isSelected && !isEditing) {
            event.preventDefault();
            event.stopPropagation();
            edit(position);
        }
    }

    function setIsDialogOpen(isOpen: boolean) {
        if (!isOpen) {
            const currentFocusPos = useDataStore.getState().focusPos;
            if (!currentFocusPos || containsPosition(currentFocusPos, position)) {
                focus(position);
            }
        }
    }

    return (
            <Popover open={isEditing} onOpenChange={setIsDialogOpen}>
                <PopoverTrigger className={TW_ChoiceCell + (readOnly ? " cursor-not-allowed" : " cursor-pointer")} onClick={onTriggerClick} onKeyDown={onEnterKeyPress} tabIndex={0}>
                    <div key={posToString(position) + "_div_" + field._key} className={"flex flex-row justify-end items-center gap-1 w-full h-full"}  >
                        {value && isArray(value) && value.length > 0 && value.map((choiceName: string) => {
                            const choice = choiceByNames.get(choiceName);
                            const choiceColor = choice?.color;
                            return (
                                <Badge key={posToString(position) + "_badge_" + field._key + "_" + choiceName} className={readOnly ? TW_ChoiceBadgeDisabled : TW_ChoiceBadge} style={{ backgroundColor: choiceColor }}>{choiceName}</Badge>
                            )
                        })}
                    </div>
                </PopoverTrigger>
                <PopoverContent className={TW_ChoiceEditionPopoverContent} side={isMobile ? "bottom" : "right"}>
                    <ChoiceEditionForm field={field} value={value} options={options} updateFieldChoices={validate} searchValue={searchValue} setSearchValue={setSearchValue} searchInputRef={searchInputRef} />
                </PopoverContent>
            </Popover>
    )

}


interface ChoiceEditionFormProps {
    field: Field,
    value: string[] | undefined,
    options: FieldChoice[],
    updateFieldChoices: (fieldChoices: string[]) => void,
    searchValue: string,
    setSearchValue: (searchValue: string) => void,
    searchInputRef: React.RefObject<HTMLInputElement>
}

const ChoiceEditionForm = memo(function ChoiceEditionForm({ field, value, options, updateFieldChoices, searchValue, setSearchValue, searchInputRef }: ChoiceEditionFormProps) {

    const { t } = useTranslation();
    const wsColor = useModelStore(state => state.wsColor);
    const wsLightColor = useModelStore(state => state.wsLightColor);

    const onOptionSelected = (selectedOption: string) => {

        const wasChoosen = value?.some(choosenItem => choosenItem === selectedOption) || false;
        const fieldChoice = options.find(option => option.name === selectedOption);

        if (!fieldChoice) {
            return;
        }

        let newChoices = value ? [...value] : [];
        if (wasChoosen) {
            newChoices = newChoices.filter(choosenItem => choosenItem !== selectedOption);
        } else {
            if (field.type === FieldType.MultiChoice) {
                newChoices.push(fieldChoice.name);
            } else {
                newChoices = [fieldChoice.name];
            }
        }

        updateFieldChoices(newChoices);
    }

    const onKeyDown = (event: React.KeyboardEvent) => {
        // event.preventDefault();
        switch (event.key) {
            case "Escape":
                event.preventDefault();
                event.stopPropagation();
                setSearchValue("");
                break;
        }
    }

    const choosenItems = value ? value : [];
    const choiceByNames = fieldChoiceByNames(options);

    const scrollStyle = {
        scrollbarColor: wsColor + " " + wsLightColor,
    }
    return (

        <Command onKeyDown={onKeyDown}>
            <CommandInput 
                placeholder={t("data.choice.search.placeholder")} 
                className={TW_CommonCommandInput} 
                autoFocus={true} 
                value={searchValue} 
                onValueChange={(value) => setSearchValue(value)} 
                ref={searchInputRef} />
            <div className={TW_ChoiceScrollArea}>
                <CommandList className="h-full max-h-full w-full scrollbar-thin scrollbar-button-hidden" style={scrollStyle}>
                    <CommandEmpty>{t("data.table.noChoiceItems")}</CommandEmpty>
                    <CommandGroup>
                        {options && options.map((option: FieldChoice) => {
                            const choiceName = option.name;
                            const choiceColor = choiceByNames.get(choiceName)?.color;
                            const isChoosen = choosenItems.some(choosenItem => choosenItem === choiceName);
                            let optionStyle = undefined;
                            if (isChoosen) {
                                optionStyle = { borderColor: choiceColor, color: choiceColor};
                            } else {
                                optionStyle = { color: choiceColor};
                            }
                            const wrapperStyle = {...optionStyle, borderWidth: 2 };
                            return (
                                <CommandItem
                                    key={choiceName}
                                    className={`${isChoosen ? TW_ChoiceItemSelected : TW_ChoiceItem}`}
                                    style={wrapperStyle}
                                    onSelect={() => onOptionSelected(choiceName)}>
                                    <span className={TW_ChoiceItemText} style={optionStyle}>{choiceName}</span>
                                </CommandItem>
                            )
                        })}
                    </CommandGroup>
                </CommandList>
            </div>
        </Command>

    )
}, (prevProps, nextProps) => {
    return prevProps.field === nextProps.field && prevProps.value === nextProps.value && prevProps.options === nextProps.options && prevProps.searchValue === nextProps.searchValue;
});
